import { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CheckboxIcon, SelectArrowDownIcon } from "@/shared/assets/icons";
import Dropdown from "../Dropdown";
import DropdownItem from "../Dropdown/DropdownItem";
import styles from "./SelectWithCheckbox.module.css";

export interface SelectWithCheckboxOption<T> {
  label: string;
  value: T;
  icon?: React.ReactNode;
  count?: number;
}

interface Props<T extends string | number> {
  options: SelectWithCheckboxOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  isSmall?: boolean;
  disabled?: boolean;
  width?: string;
}

export default function SelectWithCheckbox<T extends string | number>({
  options,
  value,
  onChange,
  placeholder,
  icon,
  isSmall = false,
  disabled = false,
  width = "w-full",
}: Props<T>) {
  const { t } = useTranslation("Common");
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t("select.placeholder");

  const hasAnyIcon = options.some((o) => o.icon);

  const validCount = value.filter((v) => options.some((o) => o.value === v)).length;

  const displayText = validCount > 0 ? `${resolvedPlaceholder} (${validCount})` : resolvedPlaceholder;

  function isSelected(optionValue: T): boolean {
    return value.includes(optionValue);
  }

  function handleToggle(optionValue: T) {
    if (isSelected(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  }

  return (
    <div className={cn("min-w-0", width)}>
      <Dropdown open={open && !disabled} onClose={() => setOpen(false)} align="left" fullWidth>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((p) => !p)}
          className={cn(
            "w-full flex items-center px-3 surface-base-fill input-box-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-200 gap-2",
            isSmall ? "h-8 radius-xs" : "py-3 radius-sm",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}>
          {icon && <span className="shrink-0 flex items-center text-grey-400">{icon}</span>}
          <span
            className={cn(
              "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left text-body-regular-sm",
              validCount === 0 ? styles.placeholder : "content-base-primary",
            )}>
            {displayText}
          </span>
          <span className="content-base-secondary w-4 h-4 flex items-center justify-center shrink-0">
            <SelectArrowDownIcon />
          </span>
        </button>

        {!disabled && (
          <div className={cn(options.length > 6 && "component-scroll-thin max-h-42")}>
            {options.map(function renderOption(opt) {
              const checked = isSelected(opt.value);
              return (
                <DropdownItem key={String(opt.value)} active={false} onClick={() => handleToggle(opt.value)}>
                  <span
                    className={cn(
                      "w-4 h-4 radius-2xs flex items-center justify-center shrink-0 pointer-events-none",
                      "bg-white checkbox-box-shadow",
                      checked && "bg-brand-500!",
                    )}>
                    {checked && <CheckboxIcon />}
                  </span>
                  {hasAnyIcon &&
                    (opt.icon ? (
                      <span className="shrink-0 flex items-center">{opt.icon}</span>
                    ) : (
                      <span className="w-4 h-4 shrink-0" />
                    ))}
                  <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                  {opt.count !== undefined && (
                    <span className="ml-auto text-label-xs content-base-low">{opt.count}</span>
                  )}
                </DropdownItem>
              );
            })}
          </div>
        )}
      </Dropdown>
    </div>
  );
}
